import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TreeModule } from 'primeng/tree';

import { ComissaoAssuntoService } from '@/core/api/sicom-api/comissoes/configuracoes/comissao-assunto.service';
import { ComissaoAssuntoResponse } from '@/core/api/sicom-api/comissoes/configuracoes/comissao-assunto.model';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
/**
 * Modal de seleção de assunto em formato de árvore.
 *
 * A hierarquia é derivada do próprio código do assunto (o pai é o código sem os
 * dois últimos dígitos), montada a partir da lista plana `id/id_pai/descrição`.
 *
 * Uso:
 *   <app-seleciona-assunto #seletor (assuntoSelecionado)="onAssunto($event)" />
 *   // no componente: this.seletor.abrir();
 */
@Component({
    selector: 'app-seleciona-assunto',
    standalone: true,
    imports: [DialogoPadraoDirective, ButtonModule, DialogModule, TreeModule],
    templateUrl: './seleciona-assunto.html',
    styleUrl: './seleciona-assunto.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelecionaAssunto {
    private readonly assuntoService = inject(ComissaoAssuntoService);
    private readonly destroyRef = inject(DestroyRef);
    private assuntosCache: ComissaoAssuntoResponse[] | null = null;

    readonly header = input('Assuntos');
    readonly assuntoSelecionado = output<ComissaoAssuntoResponse>();

    protected readonly visivel = signal(false);
    protected readonly buscando = signal(false);
    protected readonly assuntos = signal<ComissaoAssuntoResponse[]>([]);
    protected readonly selecionado = signal<TreeNode | null>(null);
    protected readonly assuntoTree = computed<TreeNode[]>(() => this.buildTree(this.assuntos()));

    abrir(): void {
        this.selecionado.set(null);
        this.visivel.set(true);
        if (!this.assuntosCache) {
            this.carregar();
        }
    }

    protected fechar(): void {
        this.visivel.set(false);
    }

    /** Confirma o assunto destacado; a seleção só é emitida ao clicar em "Selecionar". */
    protected confirmarSelecao(): void {
        const assunto = this.selecionado()?.data as ComissaoAssuntoResponse | undefined;
        if (!assunto) {
            return;
        }
        this.assuntoSelecionado.emit(assunto);
        this.visivel.set(false);
    }

    private carregar(): void {
        this.buscando.set(true);
        this.assuntoService
            .listarAssuntos()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.buscando.set(false))
            )
            .subscribe({
                next: (assuntos) => {
                    this.assuntosCache = assuntos;
                    this.assuntos.set(assuntos);
                },
                error: () => this.assuntos.set([])
            });
    }

    private buildTree(assuntos: ComissaoAssuntoResponse[]): TreeNode[] {
        const map = new Map<string, TreeNode>();
        const roots: TreeNode[] = [];

        for (const assunto of assuntos) {
            map.set(assunto.idAssunto, {
                key: assunto.idAssunto,
                label: `${assunto.idAssunto}  ${assunto.dsAssunto}`,
                data: assunto,
                children: [],
                expanded: !assunto.idAssuntoPai
            });
        }

        for (const assunto of assuntos) {
            const node = map.get(assunto.idAssunto)!;
            const temPaiValido = assunto.idAssuntoPai != null && assunto.idAssuntoPai !== assunto.idAssunto && map.has(assunto.idAssuntoPai);
            if (temPaiValido) {
                map.get(assunto.idAssuntoPai!)!.children!.push(node);
            } else {
                roots.push(node);
            }
        }

        const visitados = new Set<string>();
        const markLeaves = (nodes: TreeNode[]): void => {
            for (const node of nodes) {
                if (visitados.has(node.key!)) {
                    continue;
                }
                visitados.add(node.key!);
                if (!node.children || node.children.length === 0) {
                    delete node.children;
                    node.leaf = true;
                    node.icon = 'pi pi-tag';
                } else {
                    node.icon = 'pi pi-folder-open';
                    markLeaves(node.children);
                }
            }
        };
        markLeaves(roots);

        return roots;
    }
}
